import React, { useEffect, useMemo, useState } from 'react';
import { Save, RefreshCw, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { FormLabel } from '../../../../components/ui/form';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { Switch } from '../../../../components/ui/switch';
import { api } from '../../../../lib/api';
import { useStandort } from '../../../../components/layouts/use-standort';

type FeeItem = { enabled: boolean; amountBrutto: number | string };
// Die Aufnahmegebühr steht an der Unterrichtsform (mit Standort-Abweichung im
// Standort-Angebot) — hier gab es sie doppelt, gewirkt hat keine von beiden (T-0054).
type FeesConfig = {
  ruecklastgebuehr: FeeItem;
  kopiergebuehr: FeeItem;
};

const feeLabels: Record<keyof FeesConfig, string> = {
  ruecklastgebuehr: 'Rücklastgebühr',
  kopiergebuehr: 'Kopiergebühr',
};

const feeHints: Record<keyof FeesConfig, string> = {
  ruecklastgebuehr: 'Kommt bei einer Rücklastschrift zusätzlich zu den Bankspesen auf das Vertragskonto.',
  kopiergebuehr: 'Wird beim manuellen Buchen („Buchung erfassen“) als Vorbelegung angeboten.',
};

const parseAmount = (val: string | number): number => {
  if (typeof val === 'number') return val >= 0 ? val : 0;
  const s = (val || '').toString().trim().replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const StandortFeesSettings = () => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const defaultFees: FeesConfig = useMemo(() => ({
    ruecklastgebuehr: { enabled: false, amountBrutto: 0 },
    kopiergebuehr: { enabled: false, amountBrutto: 0 },
  }), []);

  const [fees, setFees] = useState<FeesConfig>(defaultFees);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedStandortId) {
      setFees(defaultFees);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const map = await api.get<Record<string, FeesConfig>>('/api/settings/accounting/fees');
        const current = map?.[String(selectedStandortId)];
        if (!cancelled) {
          setFees({
            ruecklastgebuehr: {
              enabled: !!current?.ruecklastgebuehr?.enabled,
              amountBrutto: current?.ruecklastgebuehr?.amountBrutto ?? 0,
            },
            kopiergebuehr: {
              enabled: !!current?.kopiergebuehr?.enabled,
              amountBrutto: current?.kopiergebuehr?.amountBrutto ?? 0,
            },
          });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Gebühren konnten nicht geladen werden';
        if (!cancelled) toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [selectedStandortId, defaultFees]);

  const updateFee = (key: keyof FeesConfig, patch: Partial<FeeItem>) => {
    setFees((prev) => ({
      ...prev,
      [key]: {
        enabled: patch.enabled ?? prev[key].enabled,
        amountBrutto: patch.amountBrutto ?? prev[key].amountBrutto,
      },
    }));
  };

  const save = async () => {
    if (!selectedStandortId) return;
    setSaving(true);
    try {
      const payload = {
        ruecklastgebuehr: { enabled: !!fees.ruecklastgebuehr.enabled, amountBrutto: parseAmount(fees.ruecklastgebuehr.amountBrutto) },
        kopiergebuehr: { enabled: !!fees.kopiergebuehr.enabled, amountBrutto: parseAmount(fees.kopiergebuehr.amountBrutto) },
      };
      const saved = await api.put<FeesConfig>(
        `/api/settings/accounting/fees/${encodeURIComponent(String(selectedStandortId))}`,
        payload,
      );
      setFees(saved);
      toast.success('Gebühren gespeichert');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Speichern fehlgeschlagen';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!selectedStandortId) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Kein Standort ausgewählt — bitte oben einen Standort wählen.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Gebühren</h1>
        <p className="text-muted-foreground">
          Rücklast- und Kopiergebühren für {selectedStandort?.name ?? 'diesen Standort'} setzen. Die Aufnahmegebühr
          steht an der Unterrichtsform bzw. im Standort-Angebot.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="mr-2 h-5 w-5" />
            Gebühren-Konfiguration
          </CardTitle>
          <CardDescription>Aktiviere die Gebühr und setze den Brutto-Betrag in EUR.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && <div className="text-sm text-muted-foreground">Lade Daten...</div>}
          {!loading && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {(Object.keys(feeLabels) as Array<keyof FeesConfig>).map((k) => (
                <div key={k} className="rounded-md border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-medium">{feeLabels[k]}</div>
                    <Switch
                      checked={!!fees[k].enabled}
                      onCheckedChange={(v) => updateFee(k, { enabled: v })}
                    />
                  </div>
                  <div className="space-y-1">
                    <FormLabel>Betrag (EUR, Brutto)</FormLabel>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={String(fees[k].amountBrutto ?? '')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFee(k, { amountBrutto: e.target.value })}
                      disabled={!fees[k].enabled}
                    />
                    <div className="text-xs text-muted-foreground">z. B. 10,00</div>
                    <p className="text-xs text-muted-foreground">{feeHints[k]}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || loading}>
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Gebühren speichern
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StandortFeesSettings;
